// Constants
import { INDICATOR, STOCK_SAVED_SCREEN_PREFIX, FUND_SAVED_SCREEN_PREFIX, SAVED_COMP_TABLE_PREFIX, SAVED_TIMESERIES_TABLE_PREFIX, SECURITYLIST_GROUP_MULTI, WATCHLIST_PREFIX, } from 'NewAngular/constants/core.constants';
/**
 * Checks whether a security list name contains the prefix for a watchlist.
 * @param securityListName
 */
export const isWatchlistPrefixPresent = (securityListName) => {
    return securityListName.includes(WATCHLIST_PREFIX);
};
/**
 * Checks whether a security list name contains the prefix for a company saved screen.
 * @param securityListName
 */
export const isCompanySavedScreenPrefixPresent = (securityListName) => {
    return securityListName.includes(STOCK_SAVED_SCREEN_PREFIX);
};
/**
 * Checks whether a security list name contains the prefix for a fund saved screen.
 * @param securityListName
 */
export const isFundSavedScreenPrefixPresent = (securityListName) => {
    return securityListName.includes(FUND_SAVED_SCREEN_PREFIX);
};
/**
 * Checks whether a security list name contains the prefix for a saved screen.
 * @param securityListName
 */
export const isSavedScreenPrefixPresent = (securityListName) => {
    return isCompanySavedScreenPrefixPresent(securityListName) || isFundSavedScreenPrefixPresent(securityListName);
};
/**
 * Checks whether a security list name contains the prefix for a saved comp table.
 * @param securityListName
 */
export const isSavedCompTablePrefixPresent = (securityListName) => {
    return securityListName.includes(SAVED_COMP_TABLE_PREFIX);
};
/**
 * Checks whether a security list name contains the prefix for a saved timeseries table.
 * @param securityListName
 */
export const isSavedTimeseriesTablePrefixPresent = (securityListName) => {
    return securityListName.includes(SAVED_TIMESERIES_TABLE_PREFIX);
};
/**
 * Returns the watchlist ID given a security list name of the form 'watchlist-12345'.
 * @param watchlistSecurityListName
 */
export const getWatchlistIdFromSecurityListName = (watchlistSecurityListName) => {
    const watchlistId = parseInt(watchlistSecurityListName.split('-')[1], 10);
    if (Number.isNaN(watchlistId)) {
        throw Error('Failed to parse watchlist ID from security list name. Make sure the security list name is in the proper format for a watchlist.');
    }
    return watchlistId;
};
/**
 * Returns the security list name/identifier for the watchlist.
 * @param watchlistId
 */
export const getWatchlistSecurityListName = (watchlistId) => {
    return `${WATCHLIST_PREFIX}${watchlistId}`;
};
// Relative URLs to open a security list in various apps
export const securityListStockScreenerUrl = (securityListName, securityGroup) => {
    return `/screener/stock/#/?securitylistFilters=method:add,name:${securityListName},group:${securityGroup},,&orderedColumns=name:currency_code,type:infoField,,&sort=display_security_id&sortDirection=asc`;
};
export const securityListFundScreenerUrl = (securityListName, securityGroup) => {
    return `/screener/mutual_fund_and_etf/#/?securitylistFilters=method:add,name:${securityListName},group:${securityGroup},,&orderedColumns=name:currency_code,type:infoField,,&sort=display_security_id&sortDirection=asc`;
};
export const securityListCompTableUrl = (securityListName, securityGroup) => {
    return `/tables/comparison/#/?securitylistFilters=method:add,name:${securityListName},group:${securityGroup},,&orderedColumns=`;
};
export const securityListTimeseriesUrl = (securityListName, securityGroup) => {
    return `/tables/timeseries/#/?securitylistFilters=method:add,name:${securityListName},group:${securityGroup},,&orderedMetrics=total_return_forward_adjusted_price,`;
};
export const securityListNewsUrl = (securityListName, securityGroup) => {
    return `/news/#/?securitylistName=${securityListName}&securityGroup=${securityGroup}`;
};
export const securityListTechChartUrl = (securityListName, securityGroup) => {
    return `/charts/technical_chart/#/?securitylistName=${securityListName}&securityGroup=${securityGroup}`;
};
export const securityListFundChartUrl = (securityListName, securityGroup) => {
    return `/charts/fundamental_chart/#/?format=indexed&securitylistName=${securityListName}&securityGroup=${securityGroup}&calcs=id:total_return_forward_adjusted_price,include:true,,`;
};
export const securityListScatterPlotUrl = (securityListName, securityGroup) => {
    return `/charts/scatter_plot/#/?securities=id:${securityListName},include:true,security_group:${securityGroup},type:security_list,,&xAxisMetric=monthly_standard_deviation_annualized_3y&yAxisMetric=annualized_three_year_total_return`;
};
export const indicatorListTimeseriesUrl = (indicatorListName) => {
    return `/tables/timeseries/#/?securitylistFilters=method:add,name:${indicatorListName},group:${INDICATOR},,&orderedInfoFields=currency_code,`;
};
export const indicatorListFundChartUrl = (indicatorListName) => {
    let url;
    if (isWatchlistPrefixPresent(indicatorListName)) {
        url = `/charts/fundamental_chart/#/?securityGroup=${SECURITYLIST_GROUP_MULTI}&securitylistName=${indicatorListName}&calcs=id:total_return_forward_adjusted_price,include:true,,`;
    }
    else {
        url = `/charts/fundamental_chart/#/?securityGroup=${INDICATOR}&securitylistName=${indicatorListName}`;
    }
    return url;
};
export const findPathForSecuritylistItem = (securitylistKey, currentMenu) => {
    let securitylistPathObject = null;
    currentMenu.some((categoryMenu) => {
        const categoryName = categoryMenu.name;
        let found = false;
        // If the category is an autocompleter category, we handle it differently.
        if (!categoryMenu.list_items && !!categoryMenu.autocompleter_name) {
            if (securitylistKey.includes(categoryMenu.key_prefix)) {
                const item = {
                    key: securitylistKey,
                    type: categoryMenu.autocompleter_name,
                    name: currentMenu[securitylistKey],
                };
                securitylistPathObject = { categoryName, item };
                found = true;
            }
            return found;
        }
        // If it is a normal category, we go through the items to see if the
        // selected item is in the category.
        return categoryMenu.list_items.some((categoryItem) => {
            let innerFound = false;
            if (categoryItem.key === securitylistKey) {
                securitylistPathObject = { categoryName, item: categoryItem };
                innerFound = true;
            }
            else if (categoryItem.sub_list) {
                const subListItem = categoryItem.sub_list.find((i) => i.key === securitylistKey);
                if (subListItem) {
                    securitylistPathObject = { categoryName, item: subListItem };
                    innerFound = true;
                }
            }
            return innerFound;
        });
    });
    return securitylistPathObject;
};
